import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { 
  UserGroupIcon, 
  DocumentTextIcon, 
  BanknotesIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { 
  notificationAPI, 
  formatCurrency, 
  formatDate,
  mutualisteAPI,
  subscriptionAPI,
  policyAPI,
  paymentAPI
} from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const AdminDashboard = () => {
  const { user } = useAuth();

  // Filtre de période simple
  const [period, setPeriod] = useState('month'); // 'month' | 'year'

  const { startDate, endDate, label } = useMemo(() => {
    const now = new Date();

    if (period === 'year') {
      const yearStart = new Date(now.getFullYear(), 0, 1);
      return {
        startDate: yearStart.toISOString().slice(0, 10),
        endDate: now.toISOString().slice(0, 10),
        label: `Année ${now.getFullYear()}`
      };
    }

    // Par défaut : ce mois
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      startDate: monthStart.toISOString().slice(0, 10),
      endDate: now.toISOString().slice(0, 10),
      label: `Mois de ${now.toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}`
    };
  }, [period]);

  // Récupérer les notifications admin (inchangé pour l'instant)
  const { data: notificationsData, isLoading: notificationsLoading } = useQuery(
    'admin-notifications',
    () => notificationAPI.getAdminNotifications({ limit: 5 })
  );

  const notifications = notificationsData?.data?.notifications || [];
  const adminStats = notificationsData?.data?.adminStats || {};

  // Stats mutualistes (adhérents actifs)
  const { data: mutualisteStatsData, isLoading: mutualisteStatsLoading } = useQuery(
    ['mutualiste-stats-admin'],
    () => mutualisteAPI.getStatistics({ type: 'ADHERENT', status: 'actif' })
  );

  const mutualisteStats = mutualisteStatsData?.data || mutualisteStatsData || {};

  // Stats souscriptions (en attente) filtrées par période
  const { data: subscriptionStatsData, isLoading: subscriptionStatsLoading } = useQuery(
    ['subscription-stats-admin', startDate, endDate],
    () => subscriptionAPI.getStats({
      status: 'PENDING',
      startDate,
      endDate
    })
  );

  const subscriptionStats = subscriptionStatsData?.data || {};

  // Souscriptions en attente (tableau top 5)
  const { data: pendingSubscriptionsData, isLoading: pendingSubscriptionsLoading } = useQuery(
    ['pending-subscriptions-admin', startDate, endDate],
    () => subscriptionAPI.getAllSubscriptions({
      page: 1,
      limit: 5,
      status: 'PENDING',
      startDate,
      endDate
    })
  );

  const pendingSubscriptions = pendingSubscriptionsData?.data?.subscriptions || pendingSubscriptionsData?.subscriptions || [];

  // Stats paiements (recettes / dépenses) sur la période
  const { data: paymentStatsData, isLoading: paymentStatsLoading } = useQuery(
    ['payment-stats-admin', startDate, endDate],
    () => paymentAPI.getStats({ startDate, endDate })
  );

  const paymentStats = paymentStatsData?.data || {};

  // Évolution mensuelle recettes / dépenses
  const { data: paymentEvolutionData, isLoading: paymentEvolutionLoading } = useQuery(
    ['payment-monthly-evolution-admin', startDate, endDate],
    () => paymentAPI.getMonthlyEvolution({ startDate, endDate })
  );

  const paymentEvolutionRaw =
    paymentEvolutionData?.data?.data || // { success, data: [...] }
    paymentEvolutionData?.data ||       // fallback si le backend renvoie directement un tableau
    [];

  const paymentEvolution = Array.isArray(paymentEvolutionRaw) ? paymentEvolutionRaw : [];

  const isLoading =
    notificationsLoading ||
    mutualisteStatsLoading ||
    subscriptionStatsLoading ||
    pendingSubscriptionsLoading ||
    paymentStatsLoading ||
    paymentEvolutionLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Tableau de bord - Administrateur
        </h1>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-gray-600">
            Bienvenue {user?.firstName}, voici un aperçu de la plateforme mutualiste.
          </p>

          {false && (
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Période :</span>
              <select
                className="form-select text-sm"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              >
                <option value="month">Ce mois</option>
                <option value="year">Cette année</option>
              </select>
              <span className="text-xs text-gray-400">{label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Bloc Répartition des mutualistes (placé en premier) */}
      <MutualistesSegmentsBlock />

      {/* Bloc Répartition des produits */}
      <ProduitsSegmentsBlock />

      {false && (
        <>
          {/* Vue d'ensemble - Statistiques */}
          <div className="bg-white rounded-xl shadow-card p-6">
            <h2 className="text-xl font-semibold text-primary-900 mb-6">
              Vue d'ensemble
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {/* Mutualistes (adhérents actifs) */}
              <div className="bg-blue-50 p-6 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-primary-900">{mutualisteStats.actifs || 0}</p>
                    <p className="text-sm text-gray-600">Adhérents actifs</p>
                  </div>
                  <UserGroupIcon className="w-12 h-12 text-primary-600" />
                </div>
              </div>

              {/* Souscriptions en attente */}
              <div className="bg-yellow-50 p-6 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-primary-900">{subscriptionStats.pending || subscriptionStats.totalPending || 0}</p>
                    <p className="text-sm text-gray-600">Souscriptions en attente</p>
                  </div>
                  <DocumentTextIcon className="w-12 h-12 text-yellow-600" />
                </div>
              </div>

              {/* Recettes période */}
              <div className="bg-green-50 p-6 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-primary-900">{formatCurrency(paymentStats.recettes || 0)}</p>
                    <p className="text-sm text-gray-600">Recettes ({period === 'month' ? 'mois' : 'année'})</p>
                  </div>
                  <BanknotesIcon className="w-10 h-10 text-green-600" />
                </div>
              </div>

              {/* Dépenses période */}
              <div className="bg-red-50 p-6 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-bold text-primary-900">{formatCurrency(paymentStats.depenses || 0)}</p>
                    <p className="text-sm text-gray-600">Dépenses ({period === 'month' ? 'mois' : 'année'})</p>
                  </div>
                  <ChartBarIcon className="w-10 h-10 text-red-600" />
                </div>
              </div>
            </div>

            {/* Graphiques */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Évolution Recettes / Dépenses */}
              <div>
                <h3 className="text-lg font-semibold text-primary-900 mb-4">
                  Évolution Recettes / Dépenses
                </h3>
                <div className="h-56 bg-gray-50 rounded-lg p-4 flex flex-col justify-between">
                  {paymentEvolution.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-400 text-sm">Aucune donnée disponible pour la période sélectionnée</p>
                    </div>
                  ) : (
                    <div className="space-y-2 overflow-y-auto">
                      {paymentEvolution.map((item) => {
                        const total = (item.recettes || 0) + (item.depenses || 0);
                        const recettesRatio = total > 0 ? (item.recettes || 0) / total : 0;
                        const depensesRatio = total > 0 ? (item.depenses || 0) / total : 0;

                        return (
                          <div key={item.month} className="text-xs">
                            <div className="flex justify-between mb-1">
                              <span className="font-medium text-gray-700">
                                {item.month}
                              </span>
                              <span className="text-gray-500">
                                R: {formatCurrency(item.recettes || 0)} / D: {formatCurrency(item.depenses || 0)}
                              </span>
                            </div>
                            <div className="w-full h-3 bg-white rounded-full border border-gray-200 overflow-hidden flex">
                              <div
                                className="bg-green-400 h-full"
                                style={{ width: `${recettesRatio * 100}%` }}
                              />
                              <div
                                className="bg-red-400 h-full"
                                style={{ width: `${depensesRatio * 100}%` }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Répartition par produit (placeholder statique pour l'instant) */}
              <div>
                <h3 className="text-lg font-semibold text-primary-900 mb-4">
                  Répartition par produit
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-secondary-500 rounded-full"></div>
                      <span className="text-sm">Épargne</span>
                    </div>
                    <span className="text-sm font-medium">60%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                      <span className="text-sm">Prêt</span>
                    </div>
                    <span className="text-sm font-medium">25%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-accent-500 rounded-full"></div>
                      <span className="text-sm">Santé</span>
                    </div>
                    <span className="text-sm font-medium">15%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Souscriptions à traiter + Notifications admin */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Souscriptions en attente */}
            <div className="bg-white rounded-xl shadow-card p-6">
              <h2 className="text-xl font-semibold text-primary-900 mb-4">
                Souscriptions à traiter
              </h2>

              {pendingSubscriptions.length === 0 ? (
                <p className="text-gray-500 text-sm">Aucune souscription en attente pour la période sélectionnée.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Date</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Mutualiste</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Produit</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-700">Montant</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {pendingSubscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                            {formatDate(sub.createdAt)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-800">
                            {sub.mutualiste ? `${sub.mutualiste.prenoms} ${sub.mutualiste.nom}` : 'N/A'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                            {sub.offer?.name || 'N/A'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-right text-gray-800">
                            {formatCurrency(sub.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Notifications admin */}
            <div className="bg-white rounded-xl shadow-card p-6">
              <h2 className="text-xl font-semibold text-primary-900 mb-6">
                Notifications admin
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {adminStats.newUsers || 3} nouveaux souscripteurs ce mois
                    </p>
                    <p className="text-sm text-gray-600">
                      Le nombre de nouveaux souscripteurs a augmenté de 15% ce mois-ci.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3 p-3 bg-yellow-50 rounded-lg">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900">
                      {adminStats.overduePayments || 2} paiements en retard
                    </p>
                    <p className="text-sm text-gray-600">
                      Attention: 2 mutualistes ont des paiements en retard de plus de 3 jours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

// Bloc séparé pour les segments mutualistes (pour garder AdminDashboard lisible)
const MutualistesSegmentsBlock = () => {
  const { data, isLoading, isError } = useQuery(
    ['mutualistes-segments'],
    () => mutualisteAPI.getSegmentStats()
  );

  const segmentsRaw = data?.data?.data || data?.data || {};
  const segmentsAdherents = segmentsRaw.adherents || {};
  const segmentsAyantsDroit = segmentsRaw.ayantsDroit || {};

  // Couleurs partagées avec DiagrammeCirculaire pour garder une cohérence
  const pieColors = ['#22C55E', '#3B82F6', '#F97316'];

  const legendStatutSource =
    segmentsAdherents.parStatut && segmentsAdherents.parStatut.length > 0
      ? segmentsAdherents.parStatut
      : (segmentsAyantsDroit.parStatut || []);

  const legendGenreSource =
    segmentsAdherents.parGenre && segmentsAdherents.parGenre.length > 0
      ? segmentsAdherents.parGenre
      : (segmentsAyantsDroit.parGenre || []);

  const statutColorMap = (legendStatutSource || []).reduce((acc, item, index) => {
    if (item?.statut) {
      acc[item.statut] = pieColors[index % pieColors.length];
    }
    return acc;
  }, {});

  const genreColorMap = (legendGenreSource || []).reduce((acc, item, index) => {
    if (item?.genre) {
      acc[item.genre] = pieColors[index % pieColors.length];
    }
    return acc;
  }, {});

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">
          Répartition des mutualistes
        </h2>
        <div className="flex items-center justify-center h-24">
          <LoadingSpinner size="medium" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">
          Répartition des mutualistes
        </h2>
        <p className="text-sm text-red-600">Erreur lors du chargement des statistiques mutualistes.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-card p-6">
      <h2 className="text-xl font-semibold text-primary-900 mb-6">
        Répartition des mutualistes
      </h2>

      {/* Grille responsive :
          - mobile : 1 colonne
          - md à lg : 2 colonnes (tranche d'âge passe sur 2e ligne)
          - xl+ : 3 colonnes (toutes les cartes sur une ligne)
      */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
        {/* Par statut - 2 diagrammes circulaires côte à côte */}
        <div className="flex flex-col space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 text-center">Par statut</h3>
          <div className="flex flex-row items-start justify-center gap-4">
            <div className="flex flex-col items-center space-y-2">
              <span className="text-xs font-medium text-gray-500">Adhérents</span>
              {segmentsAdherents.parStatut && segmentsAdherents.parStatut.length > 0 ? (
                <DiagrammeCirculaire
                  data={segmentsAdherents.parStatut}
                  labelKey="statut"
                  showLegend={false}
                  colorMap={statutColorMap}
                />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
            <div className="flex flex-col items-center space-y-2">
              <span className="text-xs font-medium text-gray-500">Ayants-droit</span>
              {segmentsAyantsDroit.parStatut && segmentsAyantsDroit.parStatut.length > 0 ? (
                <DiagrammeCirculaire
                  data={segmentsAyantsDroit.parStatut}
                  labelKey="statut"
                  showLegend={false}
                  colorMap={statutColorMap}
                />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
          </div>
          {/* Légende commune statut */}
          {legendStatutSource && legendStatutSource.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 text-xs w-full mt-0">
              {legendStatutSource.map((item, index) => (
                <div key={item.statut || index} className="inline-flex items-center">
                  <span
                    className="inline-block w-3 h-3 rounded-sm mr-1 flex-shrink-0"
                    style={{ backgroundColor: statutColorMap[item.statut] || pieColors[index % pieColors.length] }}
                  />
                  <span className="text-gray-700 whitespace-nowrap" title={item.statut}>
                    {item.statut}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Par genre - 2 diagrammes circulaires côte à côte */}
        <div className="flex flex-col space-y-2">
          <h3 className="text-sm font-semibold text-gray-700 text-center">Par genre</h3>
          <div className="flex flex-row items-start justify-center gap-4">
            <div className="flex flex-col items-center space-y-2">
              <span className="text-xs font-medium text-gray-500">Adhérents</span>
              {segmentsAdherents.parGenre && segmentsAdherents.parGenre.length > 0 ? (
                <DiagrammeCirculaire
                  data={segmentsAdherents.parGenre}
                  labelKey="genre"
                  showLegend={false}
                  colorMap={genreColorMap}
                />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
            <div className="flex flex-col items-center space-y-2">
              <span className="text-xs font-medium text-gray-500">Ayants-droit</span>
              {segmentsAyantsDroit.parGenre && segmentsAyantsDroit.parGenre.length > 0 ? (
                <DiagrammeCirculaire
                  data={segmentsAyantsDroit.parGenre}
                  labelKey="genre"
                  showLegend={false}
                  colorMap={genreColorMap}
                />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
          </div>
          {/* Légende commune genre */}
          {legendGenreSource && legendGenreSource.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 text-xs w-full mt-0">
              {legendGenreSource.map((item, index) => (
                <div key={item.genre || index} className="inline-flex items-center">
                  <span
                    className="inline-block w-3 h-3 rounded-sm mr-1 flex-shrink-0"
                    style={{ backgroundColor: genreColorMap[item.genre] || pieColors[index % pieColors.length] }}
                  />
                  <span className="text-gray-700 whitespace-nowrap" title={item.genre}>
                    {item.genre}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Par tranche d'âge - 2 histogrammes côte à côte */}
        <div className="flex flex-col space-y-3 md:col-span-2 xl:col-span-1">
          <h3 className="text-sm font-semibold text-gray-700 text-center">Par tranche d'âge</h3>
          {/*
            Layout interne :
            - Sur md (2 colonnes globales) la carte occupe 100% mais est divisée en 2 moitiés (w-1/2)
              → l'histogramme Adhérents est exactement sous "Par statut"
              → l'histogramme Ayants-droit est exactement sous "Par genre"
            - Sur xl (3 colonnes globales) la carte est plus étroite mais garde 2 moitiés internes.
          */}
          <div className="flex flex-row items-start justify-between gap-6 md:gap-10 xl:gap-14 w-full">
            <div className="flex flex-col items-center space-y-2 w-1/2 pr-4 md:pr-6">
              <span className="text-xs font-medium text-gray-500">Adhérents</span>
              {segmentsAdherents.parTrancheAge && segmentsAdherents.parTrancheAge.length > 0 ? (
                <HistogrammeTranchesAge data={segmentsAdherents.parTrancheAge} />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
            <div className="flex flex-col items-center space-y-2 w-1/2 pl-4 md:pl-6">
              <span className="text-xs font-medium text-gray-500">Ayants-droit</span>
              {segmentsAyantsDroit.parTrancheAge && segmentsAyantsDroit.parTrancheAge.length > 0 ? (
                <HistogrammeTranchesAge data={segmentsAyantsDroit.parTrancheAge} />
              ) : (
                <p className="text-xs text-gray-400">Aucune donnée.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bloc pour la répartition des produits (souscriptions / polices)
const ProduitsSegmentsBlock = () => {
  // Stats souscriptions groupées par produit+statut
  const { data: subsStatsData, isLoading: subsLoading, isError: subsError } = useQuery(
    ['produits-subscriptions-stats'],
    () => subscriptionAPI.getStats({ groupBy: 'produit_statut' })
  );

  // Stats polices groupées par produit+statut
  const { data: policiesStatsData, isLoading: policiesLoading, isError: policiesError } = useQuery(
    ['produits-policies-stats'],
    () => policyAPI.getStats({ groupBy: 'produit_statut' })
  );

  const isLoading = subsLoading || policiesLoading;
  const isError = subsError || policiesError;

  const statsSubsRaw = subsStatsData?.data || subsStatsData || {};
  const statsPoliciesRaw = policiesStatsData?.data || policiesStatsData || {};

  const parProduitStatutSubs = statsSubsRaw.parProduitStatut || [];
  const parProduitStatutPolicies = statsPoliciesRaw.parProduitStatut || [];

  // Palette partagée avec DiagrammeCirculaire
  const pieColors = ['#22C55E', '#3B82F6', '#F97316'];

  const groupByProduit = (rows) => {
    const map = new Map();
    rows.forEach((row) => {
      const produit = row.produit || 'N/A';
      const existing = map.get(produit) || [];
      existing.push(row);
      map.set(produit, existing);
    });
    return map;
  };

  const subsByProduit = groupByProduit(parProduitStatutSubs);
  const policiesByProduit = groupByProduit(parProduitStatutPolicies);

  const buildStatutLegendAndColorMap = (rows) => {
    const legend = [];
    const colorMap = {};
    rows.forEach((row) => {
      const statut = row.statut || 'Non renseigné';
      if (!legend.includes(statut)) {
        legend.push(statut);
      }
    });
    legend.forEach((statut, index) => {
      colorMap[statut] = pieColors[index % pieColors.length];
    });
    return { legend, colorMap };
  };

  const { legend: subsStatuts, colorMap: subsStatutColorMap } = buildStatutLegendAndColorMap(parProduitStatutSubs);
  const { legend: policiesStatuts, colorMap: policiesStatutColorMap } = buildStatutLegendAndColorMap(parProduitStatutPolicies);

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">
          Répartition des produits
        </h2>
        <div className="flex items-center justify-center h-24">
          <LoadingSpinner size="medium" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-white rounded-xl shadow-card p-6">
        <h2 className="text-xl font-semibold text-primary-900 mb-4">
          Répartition des produits
        </h2>
        <p className="text-sm text-red-600">Erreur lors du chargement des statistiques produits.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-card p-6">
      <h2 className="text-xl font-semibold text-primary-900 mb-6">
        Répartition des produits
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Souscriptions : un diagramme par produit, légende commune des statuts */}
        <div className="flex flex-col space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 text-center">
            Souscriptions
          </h3>
          {subsByProduit.size > 0 ? (
            <>
              <div className="flex flex-row flex-wrap items-start justify-center gap-4">
                {Array.from(subsByProduit.entries()).map(([produit, rows]) => (
                  <div key={produit} className="flex flex-col items-center space-y-1">
                    <span className="text-xs font-medium text-gray-500 text-center truncate max-w-[140px]" title={produit}>
                      {produit}
                    </span>
                    <DiagrammeCirculaire
                      data={rows}
                      labelKey="statut"
                      showLegend={false}
                      colorMap={subsStatutColorMap}
                    />
                  </div>
                ))}
              </div>
              {subsStatuts.length > 0 && (
                <div className="flex flex-wrap justify-center gap-3 text-xs w-full mt-0">
                  {subsStatuts.map((statut, index) => (
                    <div key={statut || index} className="inline-flex items-center">
                      <span
                        className="inline-block w-3 h-3 rounded-sm mr-1 flex-shrink-0"
                        style={{ backgroundColor: subsStatutColorMap[statut] || pieColors[index % pieColors.length] }}
                      />
                      <span className="text-gray-700 whitespace-nowrap" title={statut}>
                        {statut}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400 text-center">Aucune souscription pour le moment.</p>
          )}
        </div>

        {/* Polices : un diagramme par produit, légende commune des statuts */}
        <div className="flex flex-col space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 text-center">
            Polices
          </h3>
          {policiesByProduit.size > 0 ? (
            <>
              <div className="flex flex-row flex-wrap items-start justify-center gap-4">
                {Array.from(policiesByProduit.entries()).map(([produit, rows]) => (
                  <div key={produit} className="flex flex-col items-center space-y-1">
                    <span className="text-xs font-medium text-gray-500 text-center truncate max-w-[140px]" title={produit}>
                      {produit}
                    </span>
                    <DiagrammeCirculaire
                      data={rows}
                      labelKey="statut"
                      showLegend={false}
                      colorMap={policiesStatutColorMap}
                    />
                  </div>
                ))}
              </div>
              {policiesStatuts.length > 0 && (
                <div className="flex flex-wrap justify-center gap-3 text-xs w-full mt-0">
                  {policiesStatuts.map((statut, index) => (
                    <div key={statut || index} className="inline-flex items-center">
                      <span
                        className="inline-block w-3 h-3 rounded-sm mr-1 flex-shrink-0"
                        style={{ backgroundColor: policiesStatutColorMap[statut] || pieColors[index % pieColors.length] }}
                      />
                      <span className="text-gray-700 whitespace-nowrap" title={statut}>
                        {statut}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-400 text-center">Aucune police pour le moment.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Diagramme circulaire simple (camembert) en SVG avec labels dans les secteurs
// showLegend: permet de masquer la légende interne (utilisé pour les légendes communes)
// colorMap: objet optionnel { label -> couleur } pour synchroniser les couleurs avec une légende externe
const DiagrammeCirculaire = ({ data, labelKey, showLegend = true, colorMap }) => {
  const total = data.reduce((sum, item) => sum + (item.total || 0), 0) || 1;

  // Palette spécifique (vert, bleu, orange) comme dans le logo
  const baseColors = [
    '#22C55E', // vert
    '#3B82F6', // bleu
    '#F97316', // orange
  ];

  const size = 140;
  const center = size / 2;
  const radius = size / 2 - 6;

  const polarToCartesian = (cx, cy, r, angleDeg) => {
    const angleRad = ((angleDeg - 90) * Math.PI) / 180;
    return {
      x: cx + r * Math.cos(angleRad),
      y: cy + r * Math.sin(angleRad)
    };
  };

  let currentAngle = 0;
  const segments = data.map((item, index) => {
    const value = item.total || 0;
    const ratio = value / total;
    const startAngle = currentAngle;
    const endAngle = currentAngle + ratio * 360;
    currentAngle = endAngle;

    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    const start = polarToCartesian(center, center, radius, endAngle - 0.001);
    const end = polarToCartesian(center, center, radius, startAngle);

    const d = [
      `M ${center} ${center}`,
      `L ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
      'Z'
    ].join(' ');

    const midAngle = startAngle + (endAngle - startAngle) / 2;
    // Position du texte encore plus proche du centre pour rester bien lisible dans chaque secteur
    const labelPos = polarToCartesian(center, center, radius * 0.4, midAngle);
    const pourcentage = ((value / total) * 100).toFixed(1);

    const label = item[labelKey] || 'N/A';
    const colorFromMap = colorMap && label in colorMap ? colorMap[label] : null;
    const color = colorFromMap || baseColors[index % baseColors.length];

    return {
      pathD: d,
      color,
      label,
      total: value,
      pourcentage,
      labelX: labelPos.x,
      labelY: labelPos.y
    };
  });

  const containerClass = showLegend
    ? 'flex flex-col items-center justify-between h-48'
    : 'flex flex-col items-center';

  const svgClass = showLegend ? 'mb-4 drop-shadow-sm' : 'mb-1 drop-shadow-sm';

  return (
    <div className={containerClass}>
      <svg
        width={size}
        height={size}
        className={svgClass}
      >
        {segments.map((s, idx) => (
          <g key={idx}>
            <path d={s.pathD} fill={s.color} stroke="#FFFFFF" strokeWidth={1} />
            {s.total > 0 && (
              <text
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="9"
              >
                <tspan x={s.labelX} y={s.labelY - 3} fill="#FFFFFF" fontWeight="600">
                  {s.total}
                </tspan>
                <tspan x={s.labelX} y={s.labelY + 8} fill="#E5E7EB" fontWeight="500">
                  ({s.pourcentage}%)
                </tspan>
              </text>
            )}
          </g>
        ))}
      </svg>
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-3 text-xs w-full mt-1">
          {segments.map((s) => (
            <div key={s.label} className="inline-flex items-center">
              <span
                className="inline-block w-3 h-3 rounded-sm mr-1 flex-shrink-0"
                style={{ backgroundColor: s.color }}
              />
              <span className="text-gray-700 whitespace-nowrap" title={s.label}>
                {s.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Histogramme simple pour les tranches d'âge
const HistogrammeTranchesAge = ({ data }) => {
  const max = data.reduce((m, item) => Math.max(m, item.total || 0), 0) || 1;
  const maxHeight = 140; // hauteur max en pixels pour la plus grande barre

  return (
    <div className="flex flex-col items-stretch w-full">
      <div className="flex items-end space-x-0.5 h-48 mb-3 w-full">
        {data.map((item) => {
          const value = item.total || 0;
          const ratio = max > 0 ? value / max : 0;
          // Hauteur strictement proportionnelle au max, en pixels, pour une meilleure lisibilité
          const barHeight = ratio * maxHeight;
          return (
            <div key={item.tranche} className="flex-1 flex flex-col items-center">
              <div
                className="relative w-10 bg-primary-500 rounded-t-md shadow-sm flex items-center justify-center"
                style={{ height: `${barHeight}px` }}
                title={`${item.tranche} : ${value}`}
              >
                <span className="text-[9px] text-white font-semibold px-1 truncate">
                  {value}
                </span>
              </div>
              <span className="mt-1 text-[10px] text-gray-600 whitespace-nowrap">
                {item.tranche}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminDashboard;
